import { collection, doc, getDoc, getDocs, query, QueryConstraint, where } from 'firebase/firestore';
import { db } from '../../../shared/utils/firebase';
import { whereLocation } from '../../auth/managedLocations';
import { latestCountedActivityByClient } from '../../../shared/success/successRecords';
import { ReportFlags, Submission } from './alertRules';
import { isIgnition2025, LoadedClient, RawClient } from './clientRecord';
import { TaskScope } from './types';

const CLIENTS_PATH = 'bigquery_cache/client_data/comprehensive_details';
/** Firestore's `in` filter takes a bounded list; the old dashboard batched by 10 and so do we. */
const IN_BATCH = 10;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

const toLoaded = (docs: { id: string; data: () => unknown }[]): LoadedClient[] =>
  docs.map((doc) => {
    const raw = doc.data() as RawClient;
    return { docId: doc.id, clientId: String(raw.client_id ?? doc.id), raw };
  });

/** The clients a scope covers: everyone, a manager's locations, or one lead coach (string id first, then numeric). */
export async function fetchScopedClients(scope: TaskScope): Promise<LoadedClient[]> {
  const ref = collection(db, CLIENTS_PATH);
  if (scope.kind === 'all') return toLoaded((await getDocs(query(ref))).docs);
  if (scope.kind === 'location') {
    return toLoaded((await getDocs(query(ref, whereLocation('primary_location', scope.locations)))).docs);
  }
  const byString = await getDocs(query(ref, where('lead_coach_id', '==', scope.coachId)));
  if (byString.size > 0 || Number.isNaN(Number(scope.coachId))) return toLoaded(byString.docs);
  return toLoaded((await getDocs(query(ref, where('lead_coach_id', '==', Number(scope.coachId))))).docs);
}

/** Clients a coach works with without leading them (`collaborating_coach_ids` holds string ids), for the sessions the coach delivers. */
export async function fetchCollaboratorClients(coachId: string): Promise<LoadedClient[]> {
  const snapshot = await getDocs(query(collection(db, CLIENTS_PATH), where('collaborating_coach_ids', 'array-contains', coachId)));
  return toLoaded(snapshot.docs);
}

/**
 * Mirror records by client id (the documents are keyed by it), for Mindbody sessions with clients
 * this coach neither leads nor collaborates on. Ids the mirror doesn't hold are left out.
 */
export async function fetchClientsByIds(ids: string[]): Promise<LoadedClient[]> {
  const snapshots = await Promise.all(ids.map((id) => getDoc(doc(db, CLIENTS_PATH, id))));
  return toLoaded(snapshots.filter((snapshot) => snapshot.exists()));
}

/**
 * Reads a whole collection for a team scope, or only the given clients' rows
 * for a coach (whose scope is small enough to batch by id).
 */
async function readForClients(
  collectionName: string,
  field: string,
  scope: TaskScope,
  clients: LoadedClient[]
): Promise<Record<string, unknown>[]> {
  const ref = collection(db, collectionName);
  if (scope.kind !== 'coach') return (await getDocs(ref)).docs.map((doc) => ({ __id: doc.id, ...(doc.data() as object) }));
  const ids = Array.from(new Set(clients.flatMap((client) => [client.clientId, client.docId])));
  if (ids.length === 0) return [];
  const batches = await Promise.all(
    chunk(ids, IN_BATCH).map((batch) => getDocs(query(ref, where(field, 'in', batch) as QueryConstraint)))
  );
  return batches.flatMap((snapshot) => snapshot.docs.map((doc) => ({ __id: doc.id, ...(doc.data() as object) })));
}

/** `${clientId}_${check_in_type}` for every logged check-in. */
export async function fetchCompletedCheckIns(scope: TaskScope, clients: LoadedClient[]): Promise<Set<string>> {
  const rows = await readForClients('client_check_ins', 'client_id', scope, clients);
  const done = new Set<string>();
  rows.forEach((row) => {
    if (row.client_id && row.check_in_type) done.add(`${row.client_id}_${row.check_in_type}`);
  });
  return done;
}

/** Latest real success update per client (placeholders and assessment seeds ignored). */
export async function fetchLatestSuccess(scope: TaskScope, clients: LoadedClient[]): Promise<Map<string, Date>> {
  const rows = await readForClients('client_success', 'client_id', scope, clients);
  return latestCountedActivityByClient(rows.map(({ __id, ...data }) => ({ id: String(__id), data })));
}

export interface ClearedReasons {
  ignition: Set<string>;
  foundation: Set<string>;
}

/** Alert reasons already cleared, per client id. */
export async function fetchClearedReasons(scope: TaskScope, clients: LoadedClient[]): Promise<Map<string, ClearedReasons>> {
  const [ignition, foundation] = await Promise.all([
    readForClients('ignition_alerts_cleared', 'clientId', scope, clients),
    readForClients('foundation_alerts_cleared', 'clientId', scope, clients),
  ]);
  const byClient = new Map<string, ClearedReasons>();
  const add = (rows: Record<string, unknown>[], key: keyof ClearedReasons) =>
    rows.forEach((row) => {
      if (!row.clientId || !Array.isArray(row.alertReasons)) return;
      const id = String(row.clientId);
      const entry = byClient.get(id) ?? { ignition: new Set<string>(), foundation: new Set<string>() };
      (row.alertReasons as unknown[]).forEach((reason) => entry[key].add(String(reason)));
      byClient.set(id, entry);
    });
  add(ignition, 'ignition');
  add(foundation, 'foundation');
  return byClient;
}

/** Only programme clients can raise report or assessment tasks, so only they cost the per-client reads. */
export function needsProgrammeDocuments(raw: RawClient): boolean {
  return (isIgnition2025(raw) && Boolean(raw.ignition_expiration_date || raw.first_ignition_session_date)) ||
    Boolean(raw.first_foundation_session_date && raw.foundation_expiration_date);
}

/** Which generated reports exist under clients/{id}/reports (root or latest_report type). */
export async function fetchReportFlags(clientId: string): Promise<ReportFlags> {
  const snapshot = await getDocs(collection(db, 'clients', clientId, 'reports'));
  const flags: ReportFlags = { initial: false, review: false, foundationInitial: false, foundationReview: false };
  snapshot.docs.forEach((doc) => {
    const data = doc.data() as { report_type?: string; latest_report?: { report_type?: string } };
    const type = data.report_type || data.latest_report?.report_type;
    if (type === 'Initial') flags.initial = true;
    else if (type === 'Review') flags.review = true;
    else if (type === 'Foundation_Initial') flags.foundationInitial = true;
    else if (type === 'Foundation_Review') flags.foundationReview = true;
  });
  return flags;
}

/**
 * Assessment submissions for one client. Unlike getClientReportSubmissions this
 * lets a failed read throw, so it is reported instead of read as "nothing submitted".
 */
export async function fetchSubmissions(clientId: string): Promise<Submission[]> {
  const [initial, review] = await Promise.all(
    (['Initial', 'Review'] as const).map((stage) => getDocs(collection(db, 'report_submissions', clientId, stage)))
  );
  const rows = (snapshot: typeof initial, stage: 'Initial' | 'Review'): Submission[] =>
    // A stored report_type wins over the folder, as in getClientReportSubmissions, the client page and the weekly email.
    snapshot.docs.map((doc) => ({ report_type: stage, ...(doc.data() as Partial<Submission>) }) as Submission);
  return [...rows(initial, 'Initial'), ...rows(review, 'Review')];
}

/** Runs `worker` over `items` with at most `limit` in flight. */
export async function mapWithLimit<T, R>(items: T[], limit: number, worker: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const lanes = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(lanes);
  return results;
}
